import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Warning, Lock, EnvelopeSimple, X, ArrowsClockwise, ShieldCheck } from "@phosphor-icons/react";

/**
 * Iter 178 — Shared Danger-Zone 2-step confirmation modal.
 *
 * Any destructive superadmin action mounts this and calls
 * `onConfirm({challenge_id, otp})` once the user has:
 *   1. Re-entered their login password.
 *   2. Received a 6-digit OTP by email.
 *   3. Entered the OTP correctly.
 *
 * Props:
 *   action      — server-side action name (e.g. "invoices.bulk_delete").
 *                 Must match on both endpoints; the server rejects
 *                 mismatched challenges to prevent OTP-replay across
 *                 actions.
 *   summary     — one-line English description of the impact shown
 *                 on both steps ("Delete 4 210 invoices").
 *   busy        — parent-controlled disable flag while the final call
 *                 is in flight.
 *   onConfirm   — async ({challenge_id, otp}) → void  (parent throws
 *                 on failure to keep the modal open).
 *   onCancel    — closes the modal.
 */
export default function DangerZoneModal({ action, summary, busy, onConfirm, onCancel }) {
  const [step, setStep] = useState(1); // 1 = password, 2 = otp
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [email, setEmail] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const requestOtp = async () => {
    if (!password) { toast.error("Enter your password"); return; }
    setRequesting(true);
    try {
      const r = await api.post("/admin/danger/request-otp",
                                 { password, action });
      setChallengeId(r.data.challenge_id);
      setEmail(r.data.email || "");
      toast.success(r.data.message || "Code emailed. Check your inbox.");
      setPassword(""); // wipe from memory
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRequesting(false);
    }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(otp)) { toast.error("Enter the 6-digit code"); return; }
    setVerifying(true);
    try {
      await onConfirm({ challenge_id: challengeId, otp });
      // success: parent closes the modal
    } catch (_e) {
      // parent already toasted; keep the modal open for a retry
    } finally {
      setVerifying(false);
    }
  };

  const isBusy = busy || requesting || verifying;

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center px-4"
         data-testid="danger-zone-modal">
      <div className="w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#7F1D1D] to-[#B91C1C] text-white p-5">
          <button onClick={onCancel} disabled={isBusy}
                  data-testid="dz-close"
                  className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 disabled:opacity-40">
            <X size={14} weight="bold"/>
          </button>
          <div className="w-11 h-11 rounded-full bg-white/20 inline-flex items-center justify-center mb-2">
            <Warning size={20} weight="fill"/>
          </div>
          <div className="font-heading text-lg">Confirm destructive action</div>
          <div className="text-[13px] text-white/90 mt-1">{summary}</div>
        </div>

        {/* Steps */}
        <div className="px-5 pt-4 pb-2 flex items-center gap-2 text-[11px] text-[#5C5A55]">
          <span className={`h-6 px-2 inline-flex items-center gap-1 rounded-full ${step >= 1 ? "bg-[#2D5A27] text-white" : "bg-[#F7F6F2]"}`}>
            <Lock size={10}/>1. Password
          </span>
          <span className={`h-6 px-2 inline-flex items-center gap-1 rounded-full ${step >= 2 ? "bg-[#2D5A27] text-white" : "bg-[#F7F6F2]"}`}>
            <EnvelopeSimple size={10}/>2. Email OTP
          </span>
        </div>

        <div className="p-5 space-y-4">
          {step === 1 ? (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
                  Re-enter your login password
                </label>
                <input type="password" value={password}
                       onChange={e => setPassword(e.target.value)}
                       autoFocus
                       data-testid="dz-password"
                       onKeyDown={e => e.key === "Enter" && requestOtp()}
                       className="mt-1 w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm"
                       placeholder="Password"/>
                <div className="text-[11px] text-[#8A8780] mt-1">
                  We&apos;ll email a 6-digit code to your account address once the password matches.
                </div>
              </div>
              <button onClick={requestOtp} disabled={isBusy || !password}
                      data-testid="dz-request-otp"
                      className="w-full h-11 bg-[#B91C1C] hover:bg-[#7F1D1D] disabled:opacity-60 text-white rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2">
                {requesting ? <><ArrowsClockwise size={13} className="animate-spin"/>Sending code…</>
                  : <><EnvelopeSimple size={13} weight="fill"/>Send verification code</>}
              </button>
            </>
          ) : (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
                  Enter the 6-digit code
                </label>
                <input type="text" inputMode="numeric" maxLength={6} autoFocus
                       value={otp}
                       onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                       data-testid="dz-otp"
                       onKeyDown={e => e.key === "Enter" && verify()}
                       className="mt-1 w-full h-14 px-3 border border-[#E5E2D9] rounded-md text-2xl tracking-[0.5em] text-center font-mono"
                       placeholder="••••••"/>
                <div className="text-[11px] text-[#8A8780] mt-1">
                  Code sent to {email || "your email"}. Expires in 5 minutes.
                </div>
              </div>
              <button onClick={verify} disabled={isBusy || otp.length !== 6}
                      data-testid="dz-verify"
                      className="w-full h-11 bg-[#B91C1C] hover:bg-[#7F1D1D] disabled:opacity-60 text-white rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2">
                {verifying || busy
                  ? <><ArrowsClockwise size={13} className="animate-spin"/>Verifying & running…</>
                  : <><ShieldCheck size={13} weight="fill"/>Confirm & proceed</>}
              </button>
              <button onClick={() => { setStep(1); setOtp(""); setChallengeId(""); }}
                      disabled={isBusy}
                      data-testid="dz-back"
                      className="w-full h-9 text-[12px] text-[#5C5A55] hover:text-[#B91C1C] disabled:opacity-50">
                ← Change password / resend code
              </button>
            </>
          )}
          <button onClick={onCancel} disabled={isBusy}
                  data-testid="dz-cancel"
                  className="w-full h-9 text-[12px] text-[#8A8780] hover:text-[#1A1A1A] disabled:opacity-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
